/**
 * The default {@link ChunkTransport} binding — the one file in the upload client
 * that knows `XMLHttpRequest` exists.
 *
 * XHR rather than `fetch` for two reasons the protocol depends on: `fetch`
 * follows the `308 Permanent Redirect` and yields an opaque response whose
 * `Range` header is unreadable, and `fetch` has no upload-progress event at all.
 *
 * Two detectors catch the half-open socket that leaves an upload stuck:
 *
 * - `xhr.timeout` / `ontimeout` bounds the whole request
 *   (`UPLOAD_CHUNK_TIMEOUT_MS`).
 * - A progress-idle watchdog (`UPLOAD_CHUNK_STALL_TIMEOUT_MS`) abandons a
 *   chunk PUT that has stopped sending bytes, well before the total bound.
 *   Upload progress therefore drives this detector as well as the progress
 *   bar. Offset probes carry no body, so they are left to the total bound.
 *   Without it, each hung PUT would cost the whole total bound.
 *
 * Every clause of this binding is pinned by a test at this layer in
 * `xhrTransport.test.ts`. The transport resolves for **every** HTTP outcome and
 * rejects for exactly one reason — the signal aborted — which keeps
 * cancellation out of the classification switch.
 */
import { HTTP_RESUME_INCOMPLETE, UPLOAD_CHUNK_STALL_TIMEOUT_MS } from "@reflect/domain";

import type { ChunkRequest, ChunkResponse } from "./types";

/** Only a zero-anchored, total `Range` is a resume point we can act on. */
const RESUME_RANGE = /^bytes=0-(\d+)$/;

/**
 * Classify a settled `308` response into `offset-unknown`, `incomplete` or
 * `protocol-error`.
 *
 * A `308` with no readable `Range` header is `offset-unknown`, **not**
 * `nextOffset: 0` (and never `parseInt(null) === NaN`). Reading "no `Range`" as
 * "the server holds zero bytes" is true of the wire and false of the browser:
 * `getResponseHeader` returns `null` both for a header the server never sent
 * (GCS omits `Range` at offset 0) and for one it sent that the response did not
 * expose to script. Nothing at this layer can tell those apart, so it reports
 * the ambiguity and lets the driver keep the offset it already knows rather
 * than asserting a zero it cannot see. See {@link ChunkResponse}'s
 * `offset-unknown` member for which policy governs the second case.
 *
 * A present `Range` must be `bytes=0-N`; `N` is the last **committed** byte and
 * the range is inclusive, so the next byte to send is `N + 1`. Anything else — a
 * non-zero base, a wrong unit, a multi-range, an empty string — is a coordinate
 * system we did not ask for and cannot resume from.
 */
function classifyResumeIncomplete(range: string | null): ChunkResponse {
  if (range === null) {
    return { kind: "offset-unknown" };
  }
  const match = RESUME_RANGE.exec(range);
  if (match === null) {
    return { kind: "protocol-error", detail: `unparseable Range header: "${range}"` };
  }
  return { kind: "incomplete", nextOffset: Number(match[1]) + 1 };
}

/**
 * Map a settled `XMLHttpRequest`'s status line to a {@link ChunkResponse}.
 *
 * The default branch is `protocol-error`, not `complete` and not `retryable`:
 * falling through to `complete` finalizes an object the server never confirmed,
 * and falling through to `retryable` spends the whole attempt budget re-sending
 * into a response we do not understand.
 */
function classify(xhr: XMLHttpRequest): ChunkResponse {
  const { status } = xhr;
  if (status === 200 || status === 201) {
    return { kind: "complete" };
  }
  if (status === HTTP_RESUME_INCOMPLETE) {
    return classifyResumeIncomplete(xhr.getResponseHeader("Range"));
  }
  if (status === 404 || status === 410) {
    return { kind: "session-gone", status };
  }
  // 408/429 are transient congestion, not terminal — a flaky mobile network
  // produces exactly these, and ending an upload on one is a stuck upload
  // wearing an error message. They must be checked before the generic 4xx
  // branch below.
  if (status === 408 || status === 429) {
    return { kind: "retryable", reason: "server", status };
  }
  if (status >= 400 && status < 500) {
    return { kind: "client-error", status };
  }
  if (status >= 500 && status < 600) {
    return { kind: "retryable", reason: "server", status };
  }
  return { kind: "protocol-error", detail: `unexpected status ${status}` };
}

/**
 * PUT one chunk (or issue one offset probe) against the resumable session URI.
 *
 * The request is `PUT req.url` carrying exactly one request header —
 * `Content-Range: {req.contentRange}` — and `req.body` as the entire body.
 * Omitting the `Content-Range` header does not fail loudly: it silently selects
 * the receiver's one-shot whole-body path and finalizes a fragment of the
 * recording. `withCredentials` is never set: the session URI is a bearer
 * capability and a credentialed cross-origin PUT invalidates the bucket's
 * wildcard-origin CORS.
 *
 * A chunk PUT that makes no upload progress for `req.stallTimeoutMs` (default
 * `UPLOAD_CHUNK_STALL_TIMEOUT_MS`) is aborted and resolves
 * `{ kind: "retryable", reason: "stall" }`. The watchdog stands down as soon as
 * the body is fully sent, so the wait for the response stays under
 * `xhr.timeout` alone. "Fully sent" is whichever comes first: a progress event
 * that covers the whole body, or `xhr.upload` `load`. An offset probe
 * (`req.body === null`) never arms the watchdog; `xhr.timeout` bounds it.
 * A cancellation through `req.signal` still rejects, even while the watchdog is
 * armed: cancellation always wins.
 *
 * @param req - the request to issue; see {@link ChunkRequest}.
 */
export function xhrTransport(req: ChunkRequest): Promise<ChunkResponse> {
  const { url, body, contentRange, timeoutMs, signal, onProgress } = req;
  const stallTimeoutMs = req.stallTimeoutMs ?? UPLOAD_CHUNK_STALL_TIMEOUT_MS;

  return new Promise<ChunkResponse>((resolve, reject) => {
    // A signal can already be aborted at call time — the driver's
    // `throwIfAborted` and this call are not atomic, and an abort can land
    // between them. Registering a listener on an already-aborted signal never
    // fires it, so without this up-front check the request is sent and the
    // Promise hangs forever.
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }

    const xhr = new XMLHttpRequest();
    // The abort listener's ONLY job is to call `xhr.abort()`; the Promise
    // settles from `onabort` when the request actually ends.
    const onSignalAbort = (): void => xhr.abort();

    // The progress-idle watchdog. `stalled` is set BEFORE `xhr.abort()` because
    // `onabort` fires synchronously inside it and must already know why.
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let stalled = false;
    // Once the body is fully sent (or the request has settled) nothing may
    // re-arm the watchdog: the response wait belongs to `xhr.timeout`.
    let watchdogDone = false;
    const disarmStall = (): void => {
      clearTimeout(stallTimer);
      stallTimer = undefined;
    };
    const armStall = (): void => {
      if (watchdogDone) return;
      disarmStall();
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, stallTimeoutMs);
    };
    const stopStall = (): void => {
      watchdogDone = true;
      disarmStall();
    };
    const cleanup = (): void => {
      stopStall();
      signal.removeEventListener("abort", onSignalAbort);
    };

    xhr.open("PUT", url);
    // Arm the timeout BEFORE send so a half-open socket fails instead of
    // hanging.
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader("Content-Range", contentRange);
    // Always attached, even without an `onProgress` consumer: progress is what
    // keeps a slow-but-live request from being taken for a stalled one. The
    // PUT is already CORS-preflighted (it is not a simple request), so an
    // upload listener adds no preflight of its own.
    xhr.upload.onprogress = (event: ProgressEvent): void => {
      // A progress event that covers the whole body ends the upload phase.
      // Firefox fires `xhr.upload` `load` only once the response headers
      // arrive, so waiting for `load` alone would keep the watchdog armed
      // through the server's answer, which belongs to `xhr.timeout`.
      if (event.lengthComputable && event.loaded >= event.total) {
        stopStall();
      } else {
        armStall();
      }
      // `loaded` is this request's cumulative byte count; the driver owns the
      // total. Pass it through, never derive from it.
      onProgress?.(event.loaded);
    };
    // The body is fully on the wire (Chromium and WebKit fire this as the last
    // byte leaves). GCS may take a moment to acknowledge the final chunk, so the
    // watchdog stands down and `xhr.timeout` bounds the rest.
    xhr.upload.onload = stopStall;

    xhr.onload = (): void => {
      cleanup();
      resolve(classify(xhr));
    };
    xhr.onerror = (): void => {
      cleanup();
      resolve({ kind: "retryable", reason: "network" });
    };
    xhr.ontimeout = (): void => {
      cleanup();
      resolve({ kind: "retryable", reason: "timeout" });
    };
    xhr.onabort = (): void => {
      cleanup();
      // Cancellation wins over a stall that fired in the same tick: a
      // deliberate abort must never come back as a retry.
      if (stalled && !signal.aborted) {
        resolve({ kind: "retryable", reason: "stall" });
        return;
      }
      reject(signal.reason);
    };

    signal.addEventListener("abort", onSignalAbort);
    // Armed immediately before send, and only for a chunk PUT. A probe has no
    // body and so no progress events: the watchdog would bound its whole round
    // trip at the stall window. After a stall the driver sends only probes
    // until one answers, so on a silent network that would cut the run's whole
    // tolerance to a handful of stall windows. `xhr.timeout` bounds a probe
    // instead.
    if (body !== null) armStall();
    xhr.send(body);
  });
}
